/**
 * Base URL absoluta para canonical, og:url e twitter:url.
 * Nunca retorna path relativo; quando não houver origem conhecida, usa fallback documentado.
 *
 * Ordem de resolução:
 * 1. VITE_APP_URL (normalizada, sem barra final) se existir e for URL absoluta.
 * 2. window.location.origin se estiver no browser.
 * 3. FALLBACK_BASE_URL quando nenhum dos dois existir (ex.: build, SSR ou teste).
 *    Em produção, configure VITE_APP_URL para que o fallback não seja usado.
 */
import { FABCHAT_FEATURE_LIST } from "@/lib/geo-product";
import {
  FABCHAT_CNPJ,
  FABCHAT_LEGAL_NAME,
  FABCHAT_SUPPORT_EMAIL,
  FABCHAT_SUPPORT_PHONE_E164,
} from "@/lib/org-contact";

const FALLBACK_BASE_URL = "https://fabchat.com.br";

function isAbsoluteUrl(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith("http://") || trimmed.startsWith("https://");
}

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

export function getBaseUrl(): string {
  const env = import.meta.env.VITE_APP_URL as string | undefined;
  if (env && typeof env === "string") {
    const normalized = normalizeBaseUrl(env);
    if (normalized && isAbsoluteUrl(normalized)) {
      return normalized;
    }
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return FALLBACK_BASE_URL;
}

/** Caminho público da imagem padrão OG/Twitter (1.91:1 recomendado). URL absoluta = baseUrl + isto. */
export const DEFAULT_OG_IMAGE_PATH = "/og-fabchat.png";

/**
 * Monta URL absoluta para og:image / twitter:image.
 * Aceita URL absoluta; paths relativos (ex.: /og-fabchat.png) são prefixados com baseUrl.
 */
export function resolveOgImageUrl(baseUrl: string, image?: string): string {
  const raw = (image?.trim() || DEFAULT_OG_IMAGE_PATH).trim();
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${normalizedBase}${path}`;
}

const ORGANIZATION_NAME = "FabChat";
const INDEX_DESCRIPTION =
  "Crie e opere agentes de IA conectados ao WhatsApp e Instagram. Automatize vendas, suporte e atendimento com inteligência artificial.";

/** Perfis oficiais (mesmos links do rodapé) para JSON-LD sameAs. */
export const ORGANIZATION_SAME_AS = [
  "https://www.instagram.com/fab.chat/",
  "https://www.linkedin.com/company/108232609/",
  "https://www.facebook.com/profile.php?id=61572604333403",
] as const;

/**
 * Objeto JSON-LD Organization para a home (sem reviews nem dados inventados).
 */
export function buildOrganizationSchema(baseUrl: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: ORGANIZATION_NAME,
    legalName: FABCHAT_LEGAL_NAME,
    taxID: FABCHAT_CNPJ,
    url: baseUrl,
    logo: `${baseUrl}/logo.svg`,
    sameAs: [...ORGANIZATION_SAME_AS],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: FABCHAT_SUPPORT_EMAIL,
        telephone: FABCHAT_SUPPORT_PHONE_E164,
        availableLanguage: ["Portuguese", "English"],
        areaServed: "BR",
      },
    ],
    address: {
      "@type": "PostalAddress",
      addressCountry: "BR",
    },
  };
}

/**
 * Objeto JSON-LD SoftwareApplication para a home (sem aggregateRating).
 */
export function buildSoftwareApplicationSchema(
  baseUrl: string,
  description: string = INDEX_DESCRIPTION
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: ORGANIZATION_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web Browser",
    description,
    url: baseUrl,
    featureList: [...FABCHAT_FEATURE_LIST],
    offers: {
      "@type": "Offer",
      url: `${baseUrl}/planos`,
    },
  };
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

/**
 * Objeto JSON-LD BreadcrumbList (itens com nome e path; baseUrl é aplicado aos paths).
 */
export function buildBreadcrumbSchema(
  baseUrl: string,
  items: BreadcrumbItem[]
): Record<string, unknown> {
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${normalizedBase}${item.path.startsWith("/") ? item.path : `/${item.path}`}`,
    })),
  };
}

/**
 * Objeto JSON-LD FAQPage usando Q/A já existentes no site.
 * Importante: não inventa conteúdo; apenas converte o array recebido.
 */
export function buildFAQPageSchema(
  items: Array<{ question: string; answer: string }>
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      },
    })),
  };
}

/**
 * JSON-LD WebSite schema (homepage only). Sem SearchAction: o site não tem busca interna.
 */
export function buildWebSiteSchema(baseUrl: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "FabChat",
    url: baseUrl,
  };
}

/**
 * JSON-LD WebPage schema for any page.
 */
export function buildWebPageSchema(
  baseUrl: string,
  opts: { path: string; title: string; description: string }
): Record<string, unknown> {
  const url = `${baseUrl}${opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    url,
    name: opts.title,
    description: opts.description,
    isPartOf: {
      "@type": "WebSite",
      url: baseUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "FabChat",
      url: baseUrl,
    },
  };
}
